import { useMemo, useState } from "react"
import { Box, Chip, Container, Stack, Typography } from "@mui/material"

import CarCard from "../../../components/CarCard"
import EmptyState from "../../../components/EmptyState"
import PageCtaBand from "../../../components/PageCtaBand"
import StyledPagination from "../../../components/StyledPagination"
import { useEnquire } from "../../../components/EnquireModal"
import { PHONE_DISPLAY, PHONE_HREF } from "../../../constants/contactInfo"
import { useAllCars, useClientPagination } from "../../../hooks/useCars"
import { overlineSx, LIGHT_SECTION_BACKGROUND } from "../../../styles/sectionStyles"
import {
    DEFAULT_VEHICLE_FILTERS,
    filterVehicles,
    getFilterOptions,
    hasActiveFilters,
} from "../../../utils/vehicleFilters"
import VehicleFilters from "./VehicleFilters"

const PAGE_SIZE = 9

const FleetPage = () => {
    const openEnquire = useEnquire()
    const { cars: allCars, loading } = useAllCars()
    const [filters, setFilters] = useState({ ...DEFAULT_VEHICLE_FILTERS })

    const filterOptions = useMemo(() => getFilterOptions(allCars), [allCars])
    const filteredCars = useMemo(() => filterVehicles(allCars, filters), [allCars, filters])

    const { paginatedItems: paginatedCars, currentPage, setCurrentPage, totalPages } =
        useClientPagination(filteredCars, PAGE_SIZE, filters)

    const totalCars = allCars.length
    const filteredCount = filteredCars.length
    const rangeStart = filteredCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredCount)
    const filtersActive = hasActiveFilters(filters)

    return (
        <>
            <Box
                component="section"
                id="vehicles-list"
                sx={{
                    position: "relative",
                    py: { xs: 6, md: 9 },
                    background: LIGHT_SECTION_BACKGROUND,
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        spacing={1.5}
                        textAlign="center"
                        mb={{ xs: 4, md: 5 }}
                        maxWidth={640}
                        mx="auto"
                        data-aos="fade-up"
                        data-aos-duration="800"
                    >
                        <Typography variant="overline" sx={overlineSx}>
                            Driver-ready favourites
                        </Typography>
                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{ color: "#000435", lineHeight: 1.15 }}
                        >
                            Choose your hire car
                        </Typography>
                        <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
                            Every vehicle is licensed for Private Hire work with a specific council.
                            Filter by your licensing authority, then by make, fuel type, platform tags,
                            or insurance — and enquire on the car you want.
                        </Typography>
                    </Stack>

                    {!loading && totalCars > 0 && (
                        <VehicleFilters
                            filters={filters}
                            options={filterOptions}
                            onChange={setFilters}
                            resultCount={filteredCount}
                        />
                    )}

                    {totalCars > 0 && (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                            spacing={2}
                            mb={3}
                            data-aos="fade-up"
                            data-aos-duration="800"
                        >
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    label={`${totalCars} vehicle${totalCars === 1 ? "" : "s"} listed`}
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: "rgba(0, 163, 255, 0.12)",
                                        color: "#000435",
                                    }}
                                />
                                {filtersActive && (
                                    <Chip
                                        label={`${filteredCount} matching`}
                                        sx={{
                                            fontWeight: 600,
                                            bgcolor: "rgba(0, 4, 53, 0.06)",
                                            color: "#000435",
                                        }}
                                    />
                                )}
                            </Stack>

                            {filteredCount > 0 && (
                                <Typography variant="body2" color="text.secondary" textAlign="right">
                                    Showing {rangeStart}–{rangeEnd} of {filteredCount}
                                </Typography>
                            )}
                        </Stack>
                    )}

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 2.5,
                            width: "100%",
                            minWidth: 0,
                            minHeight: loading ? 280 : undefined,
                            opacity: loading ? 0.55 : 1,
                            transition: "opacity 0.2s ease",
                        }}
                    >
                        {paginatedCars.map((car, index) => (
                            <CarCard
                                key={car._id.toString()}
                                vehicle={car}
                                aos="fade-up"
                                aosDelay={(index % 3) * 80}
                            />
                        ))}
                    </Box>

                    {!loading && totalCars === 0 && (
                        <EmptyState
                            title="No vehicles listed right now"
                            description="Call or send an enquiry and we'll let you know what's coming available soon."
                            actions={[
                                {
                                    label: "Enquire now",
                                    onClick: () => openEnquire?.(),
                                },
                                {
                                    label: `Call ${PHONE_DISPLAY}`,
                                    href: PHONE_HREF,
                                    variant: "outlined",
                                },
                            ]}
                        />
                    )}

                    {!loading && totalCars > 0 && filteredCount === 0 && (
                        <EmptyState
                            title="No vehicles match your filters"
                            description="Try changing your filters, or get in touch and we'll help you find a suitable hire car."
                            actions={[
                                {
                                    label: "Clear filters",
                                    onClick: () => setFilters({ ...DEFAULT_VEHICLE_FILTERS }),
                                },
                            ]}
                        />
                    )}

                    {!loading && filteredCount > PAGE_SIZE && (
                        <StyledPagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_e, page) => {
                                setCurrentPage(page)
                                document.getElementById("vehicles-list")?.scrollIntoView({
                                    behavior: "smooth",
                                })
                            }}
                        />
                    )}
                </Container>
            </Box>

            <PageCtaBand
                overline="Need a hand?"
                title="Not sure which car suits you?"
                description="Tell us how you drive — platform, mileage, fuel preference — and we'll recommend a suitable car from what's available."
                gradientPosition="80% 50%"
                actions={[
                    { label: "Enquire now", onClick: () => openEnquire?.() },
                    {
                        label: `Call ${PHONE_DISPLAY}`,
                        href: PHONE_HREF,
                        variant: "outlined",
                    },
                ]}
            />
        </>
    )
}

export default FleetPage
